import { useEffect, useRef, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ExpiryDateInput from "@/components/ExpiryDateInput";
import { useMarginPreference } from "@/lib/marginPreference";
import { BARCODE_ENABLED } from "@/lib/featureFlags";

const emptyForm = {
  name: "",
  category: "",
  weight: "",
  batch: "",
  barcode: "",
  plu: "",
  supplier_code: "",
  expiry_date: "",
  sale_unit: "piece",
  stock: 0,
  stock_kg: "",
  min_stock: "",
  price: 0,
  price_per_kg: 0,
  cost_price: 0,
  cost_per_kg: 0,
  supplier: "",
  notes: "",
  is_fresh: false,
};

export default function ProductSheet({ open, onOpenChange, product, categories, onSave, onGoToLots }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  // Snapshot dei valori del form all'apertura, per rilevare modifiche non
  // salvate quando l'operatore clicca "Vai ai lotti" su prodotto multi-lotto.
  const initialFormRef = useRef(emptyForm);
  const [confirmLeaveOpen, setConfirmLeaveOpen] = useState(false);

  // Unica condizione multi-lotto, definita qui e riusata OVUNQUE (disable dei
  // campi cache, banner, filtro payload in submit). Regole:
  //   - prodotto NUOVO (senza id) → MAI multiLot (creazione: campi liberi);
  //   - prodotto esistente con lots array di 2+ → multiLot;
  //   - prodotto esistente con lots assente, null o non-array → multiLot per
  //     difesa in profondità (non trattare "sconosciuto" come "zero lotti");
  //   - prodotto esistente con lots array di 0 o 1 → non multiLot.
  const isExistingProduct = !!product?.id;
  const lotsIsArray = Array.isArray(product?.lots);
  const lotsCount = lotsIsArray ? product.lots.length : null;
  const multiLot =
    isExistingProduct && (!lotsIsArray || lotsCount >= 2);
  // Elenco dei campi cache che il backend tratta come cache dei lotti.
  // Se multiLot=true vengono OMESSI dal payload PUT (backend li considera
  // "non toccare", vedi server.py:2491 e _apply_cache_fields_upsert).
  const CACHE_FIELDS = [
    "batch",
    "expiry_date",
    "stock",
    "stock_kg",
    "cost_price",
    "cost_per_kg",
  ];

  useEffect(() => {
    if (product) {
      const isWeight = product.sale_unit === "weight";
      // Formatta a 3 decimali fissi SOLO all'apertura dello sheet per prodotti a peso
      // (coerenza visiva tra Giacenza e Giacenza minima). Non tocca il valore salvato.
      const formatKg = (v) =>
        v !== null && v !== undefined ? (isWeight ? Number(v).toFixed(3) : String(v)) : "";
      const next = {
        name: product.name || "",
        category: product.category || "",
        weight: product.weight || "",
        batch: product.batch || "",
        barcode: product.barcode || "",
        plu: product.plu || "",
        supplier_code: product.supplier_code || "",
        expiry_date: product.expiry_date || "",
        sale_unit: isWeight ? "weight" : "piece",
        stock: product.stock || 0,
        stock_kg: formatKg(product.stock_kg),
        min_stock: formatKg(product.min_stock),
        price: product.price || 0,
        price_per_kg: product.price_per_kg || 0,
        cost_price: product.cost_price || 0,
        cost_per_kg: product.cost_per_kg || 0,
        supplier: product.supplier || "",
        notes: product.notes || "",
        is_fresh: !!product.is_fresh,
      };
      setForm(next);
      initialFormRef.current = next;
    } else {
      setForm(emptyForm);
      initialFormRef.current = emptyForm;
    }
  }, [product, open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Dirty check per la navigazione "Vai ai lotti" quando il prodotto ha 2+ lotti:
  // i campi non-cache (nome, categoria, prezzo, soglia, note...) potrebbero avere
  // modifiche non ancora salvate. In quel caso mostriamo un AlertDialog di conferma
  // invece di chiudere silenziosamente lo sheet perdendo i dati.
  const isDirty = () =>
    JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

  const goToLotsNow = () => {
    onGoToLots?.(product?.id);
    onOpenChange(false);
  };

  const handleGoToLotsClick = () => {
    if (isDirty()) {
      setConfirmLeaveOpen(true);
      return;
    }
    goToLotsNow();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.category) return;
    setSaving(true);
    // Normalizza stock_kg accettando virgola come separatore decimale.
    const parsedStockKg = (() => {
      const raw = String(form.stock_kg ?? "").replace(",", ".").trim();
      if (raw === "") return null;
      const n = Number(raw);
      return Number.isFinite(n) && n >= 0 ? n : null;
    })();
    const parsedMinStock = (() => {
      const raw = String(form.min_stock ?? "").replace(",", ".").trim();
      if (raw === "") return null;
      const n = Number(raw);
      return Number.isFinite(n) && n >= 0 ? n : null;
    })();
    const payload = {
      ...form,
      sale_unit: form.sale_unit === "weight" ? "weight" : "piece",
      stock: Number(form.stock) || 0,
      stock_kg: parsedStockKg,
      min_stock: parsedMinStock,
      price: Number(form.price) || 0,
      price_per_kg: Number(form.price_per_kg) > 0 ? Number(form.price_per_kg) : null,
      cost_price: Number(form.cost_price) > 0 ? Number(form.cost_price) : null,
      cost_per_kg: Number(form.cost_per_kg) > 0 ? Number(form.cost_per_kg) : null,
      barcode: form.barcode?.trim() || null,
      plu: form.plu?.trim() || null,
      supplier_code: form.supplier_code?.trim() || null,
      expiry_date: form.expiry_date || null,
    };
    // Con multiLot: OMETTIAMO i 6 campi cache dal body. Il backend
    // (server.py:2491) filtra i None con model_dump()+if v is not None, quindi
    // "omesso" == "non toccare" (verificato empiricamente: lotti e flat
    // identici prima/dopo un PUT senza cache-fields). Così ignored_fields
    // resta [] e il toast non diventa giallo per campi che l'operatore
    // non ha nemmeno potuto modificare (erano disabled).
    if (multiLot) {
      for (const k of CACHE_FIELDS) {
        delete payload[k];
      }
    }
    await onSave(payload);
    setSaving(false);
  };

  // Live margin preview for the user while editing
  const livePrice = Number(form.price_per_kg) || Number(form.price) || 0;
  const liveCost = Number(form.cost_per_kg) || Number(form.cost_price) || 0;
  const liveMargin = liveCost > 0 && livePrice > 0
    ? Math.round(((livePrice - liveCost) / liveCost) * 100)
    : null;
  const showMargin = useMarginPreference();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto bg-white">
        <SheetHeader>
          <SheetTitle style={{ fontFamily: "Work Sans, sans-serif" }}>
            {product?.id ? "Modifica prodotto" : "Nuovo prodotto"}
          </SheetTitle>
          <SheetDescription>
            Compila i dati. Nome e categoria sono obbligatori.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={submit} className="space-y-4 mt-6">
          <Field label="Nome prodotto *" htmlFor="p-name">
            <Input
              id="p-name"
              data-testid="product-name-input"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Es: Mozzarella di Bufala 250g"
              required
            />
          </Field>

          <Field label="Categoria *" htmlFor="p-cat">
            <Select value={form.category} onValueChange={(v) => update("category", v)}>
              <SelectTrigger data-testid="product-category-select" id="p-cat">
                <SelectValue placeholder="Seleziona categoria" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
                <SelectItem value="Altro">Altro</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          {/* Banner multi-lotto: una sola riga in tutto il form, sopra la prima
              sezione con campi bloccati. Spiega perché lotto/scadenza/giacenza/costo
              non sono modificabili e offre il ponte diretto alla LotsList. */}
          {multiLot && (
            <div
              data-testid="product-multilot-banner"
              className="rounded-lg border border-[#F2DEB0] bg-[#FDF6E3] p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            >
              <p className="text-sm text-[#5C4A18] leading-snug">
                {lotsIsArray && lotsCount >= 2 ? (
                  <>
                    Questo prodotto ha <span data-testid="product-multilot-count">{lotsCount}</span> lotti:
                    lotto, scadenza, giacenza e costo si modificano dal singolo lotto.
                  </>
                ) : (
                  <span data-testid="product-multilot-unknown">
                    I dati dei lotti di questo prodotto non sono disponibili ora.
                    Per sicurezza, lotto, scadenza, giacenza e costo si modificano dal singolo lotto.
                  </span>
                )}
              </p>
              <Button
                type="button"
                data-testid="product-multilot-goto-lots"
                onClick={handleGoToLotsClick}
                className="bg-[#2E4A3B] hover:bg-[#21382C] text-white min-h-[44px] px-4 whitespace-normal shrink-0"
              >
                Vai ai lotti
              </Button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Peso / Volume" htmlFor="p-weight">
              <Input
                id="p-weight"
                data-testid="product-weight-input"
                value={form.weight}
                onChange={(e) => update("weight", e.target.value)}
                placeholder="500g, 1L..."
              />
            </Field>
            <Field label="Lotto" htmlFor="p-batch">
              <Input
                id="p-batch"
                data-testid="product-batch-input"
                value={form.batch}
                onChange={(e) => update("batch", e.target.value)}
                placeholder="LB-2302"
                disabled={multiLot}
                className={multiLot ? "bg-[#F4F1ED] text-[#8E918C] cursor-not-allowed" : undefined}
              />
            </Field>
          </div>

          {BARCODE_ENABLED && (
          <Field label="Barcode (EAN/UPC)" htmlFor="p-barcode">
            <Input
              id="p-barcode"
              data-testid="product-barcode-input"
              value={form.barcode}
              onChange={(e) => update("barcode", e.target.value)}
              placeholder="Es: 8001505005707 (opzionale)"
              inputMode="numeric"
            />
          </Field>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="PLU (codice interno)" htmlFor="p-plu">
              <Input
                id="p-plu"
                data-testid="product-plu-input"
                value={form.plu}
                onChange={(e) => update("plu", e.target.value)}
                placeholder="Es: 270"
                inputMode="numeric"
              />
            </Field>
            <Field label="Codice fornitore" htmlFor="p-supcode">
              <Input
                id="p-supcode"
                data-testid="product-supplier-code-input"
                value={form.supplier_code}
                onChange={(e) => update("supplier_code", e.target.value.toUpperCase())}
                placeholder="Es: 04FC0200V"
                className="font-mono uppercase"
              />
            </Field>
          </div>

          <Field label="Scadenza" htmlFor="p-exp">
            <ExpiryDateInput
              testid="product-expiry-input"
              value={form.expiry_date}
              onChange={(v) => update("expiry_date", v)}
              category={form.category}
              name={form.name}
              disabled={multiLot}
            />
          </Field>

          {/* Toggle Vendita a: Pezzo / Peso (kg) */}
          <div>
            <Label className="text-xs font-semibold text-[#5C5F5A] uppercase tracking-wider mb-2 block">Vendita a</Label>
            <div className="inline-flex rounded-lg border border-[#EAE7E1] bg-[#F4F1ED] p-1" data-testid="product-sale-unit-toggle">
              <button
                type="button"
                data-testid="product-sale-unit-piece"
                onClick={() => update("sale_unit", "piece")}
                className={`px-4 py-1.5 text-sm rounded-md font-medium transition-colors ${
                  form.sale_unit === "piece"
                    ? "bg-white text-[#1A1C1A] shadow-sm"
                    : "text-[#5C5F5A] hover:text-[#1A1C1A]"
                }`}
              >
                Pezzo
              </button>
              <button
                type="button"
                data-testid="product-sale-unit-weight"
                onClick={() => update("sale_unit", "weight")}
                className={`px-4 py-1.5 text-sm rounded-md font-medium transition-colors ${
                  form.sale_unit === "weight"
                    ? "bg-white text-[#1A1C1A] shadow-sm"
                    : "text-[#5C5F5A] hover:text-[#1A1C1A]"
                }`}
              >
                Peso (kg)
              </button>
            </div>
          </div>

          {/* Giacenza: condizionale al sale_unit. I valori dell'altro ramo restano
              in form ma nascosti, così non si perdono al toggle. */}
          <div className="grid grid-cols-2 gap-3">
            {form.sale_unit === "weight" ? (
              <Field label="Giacenza (kg)" htmlFor="p-stock-kg">
                <Input
                  id="p-stock-kg"
                  data-testid="product-stock-kg-input"
                  type="text"
                  inputMode="decimal"
                  placeholder="Es: 2,300"
                  value={form.stock_kg}
                  onChange={(e) => update("stock_kg", e.target.value)}
                  disabled={multiLot}
                  className={multiLot ? "bg-[#F4F1ED] text-[#8E918C] cursor-not-allowed" : undefined}
                />
              </Field>
            ) : (
              <Field label="Stock (unità)" htmlFor="p-stock">
                <Input
                  id="p-stock"
                  data-testid="product-stock-input"
                  type="number"
                  min="0"
                  value={form.stock}
                  onChange={(e) => update("stock", e.target.value)}
                  disabled={multiLot}
                  className={multiLot ? "bg-[#F4F1ED] text-[#8E918C] cursor-not-allowed" : undefined}
                />
              </Field>
            )}
            <Field
              label={
                form.sale_unit === "weight"
                  ? "Giacenza minima (kg, opzionale)"
                  : "Giacenza minima (pz, opzionale)"
              }
              htmlFor="p-min-stock"
            >
              <Input
                id="p-min-stock"
                data-testid="product-min-stock-input"
                type="text"
                inputMode="decimal"
                placeholder={form.sale_unit === "weight" ? "es. 0,5" : "es. 5"}
                value={form.min_stock ?? ""}
                onChange={(e) => update("min_stock", e.target.value)}
              />
              <div className="text-[10px] text-[#8E918C] mt-1 leading-snug">
                {form.sale_unit === "weight"
                  ? "Quando i kg disponibili scendono a o sotto questo valore ricevi un avviso."
                  : "Quando lo stock scende a o sotto questo numero ricevi un avviso."}
                {" Lascia vuoto se non vuoi soglia personalizzata."}
              </div>
            </Field>
          </div>

          {/* Prezzo di vendita: condizionale al sale_unit */}
          {form.sale_unit === "weight" ? (
            <Field label="Prezzo al kg (€/kg)" htmlFor="p-ppk">
              <Input
                id="p-ppk"
                data-testid="product-price-per-kg-input"
                type="number"
                step="0.01"
                min="0"
                placeholder="Es: 24,90"
                value={form.price_per_kg}
                onChange={(e) => update("price_per_kg", e.target.value)}
              />
            </Field>
          ) : (
            <Field label="Prezzo unitario (€/pz)" htmlFor="p-price">
              <Input
                id="p-price"
                data-testid="product-price-input"
                type="number"
                step="0.01"
                min="0"
                value={form.price}
                onChange={(e) => update("price", e.target.value)}
              />
            </Field>
          )}

          {/* Acquisto / Margine */}
          <div className="pt-2 border-t border-[#EAE7E1]">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-semibold text-[#5C5F5A] uppercase tracking-wider">Costo di acquisto (da fornitore)</Label>
              {liveMargin !== null && showMargin && (
                <span
                  data-testid="product-margin-preview"
                  className={`text-[11px] font-semibold px-2 py-0.5 rounded-md border ${
                    liveMargin < 10
                      ? "bg-[#FCEAE8] text-[#8E3B2A] border-[#F2C4BE]"
                      : liveMargin < 30
                      ? "bg-[#FDF6E3] text-[#8B6518] border-[#F2DEB0]"
                      : "bg-[#E8F3EC] text-[#2B5A3F] border-[#C5E1D1]"
                  }`}
                >
                  Margine: {liveMargin >= 0 ? "+" : ""}{liveMargin}%
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {form.sale_unit === "weight" ? (
                <Field label="Acquisto (€/kg)" htmlFor="p-cpk">
                  <Input
                    id="p-cpk"
                    data-testid="product-cost-per-kg-input"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Opzionale"
                    value={form.cost_per_kg}
                    onChange={(e) => update("cost_per_kg", e.target.value)}
                    disabled={multiLot}
                    className={multiLot ? "bg-[#F4F1ED] text-[#8E918C] cursor-not-allowed" : undefined}
                  />
                </Field>
              ) : (
                <Field label="Acquisto (€/pz)" htmlFor="p-cost">
                  <Input
                    id="p-cost"
                    data-testid="product-cost-price-input"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Opzionale"
                    value={form.cost_price}
                    onChange={(e) => update("cost_price", e.target.value)}
                    disabled={multiLot}
                    className={multiLot ? "bg-[#F4F1ED] text-[#8E918C] cursor-not-allowed" : undefined}
                  />
                </Field>
              )}
            </div>
            <p className="text-[11px] text-[#8E918C] mt-1.5">
              Se compili costo + vendita, l&apos;app calcola il margine e lo mostra nel magazzino.
            </p>
          </div>

          <Field label="Fornitore" htmlFor="p-sup">
            <Input
              id="p-sup"
              data-testid="product-supplier-input"
              value={form.supplier}
              onChange={(e) => update("supplier", e.target.value)}
              placeholder="Nome fornitore"
            />
          </Field>

          <Field label="Note" htmlFor="p-notes">
            <Textarea
              id="p-notes"
              data-testid="product-notes-input"
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              rows={3}
              placeholder="Note aggiuntive"
            />
          </Field>

          <div className="flex items-center justify-between p-3 rounded-lg bg-[#F4F1ED] border border-[#EAE7E1]">
            <div>
              <div className="text-sm font-medium">Prodotto fresco</div>
              <div className="text-xs text-[#8E918C]">Priorità alta sugli avvisi scadenza</div>
            </div>
            <Switch
              data-testid="product-fresh-switch"
              checked={form.is_fresh}
              onCheckedChange={(v) => update("is_fresh", v)}
            />
          </div>

          <SheetFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-[#EAE7E1]"
            >
              Annulla
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="product-save-btn"
              className="bg-[#2E4A3B] hover:bg-[#21382C] text-white"
            >
              {saving ? "Salvataggio..." : "Salva"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
      <AlertDialog open={confirmLeaveOpen} onOpenChange={setConfirmLeaveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Modifiche non salvate</AlertDialogTitle>
            <AlertDialogDescription>
              Hai modifiche non ancora salvate in questo form. Se apri i lotti adesso,
              le perderai. Vuoi continuare comunque?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="product-multilot-leave-cancel">
              Torna al form
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="product-multilot-leave-confirm"
              onClick={() => {
                setConfirmLeaveOpen(false);
                goToLotsNow();
              }}
              className="bg-[#C65D47] hover:bg-[#A64B38]"
            >
              Continua senza salvare
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="text-xs font-medium text-[#5C5F5A] uppercase tracking-wider">
        {label}
      </Label>
      {children}
    </div>
  );
}
